import * as z from 'zod/mini'
import { isoDateTime } from '../../schemas/common'
import { customerFields } from '../../schemas/customer'
import { personFields } from '../../schemas/person'
import { CustomerStatus } from '../../types'

/** A customer URL (`.../customers/:customerId`), looked up by `CustomerGuard` within the premise. */
export const customerParams = z.object({ customerId: z.uuid() })
export type CustomerParams = z.infer<typeof customerParams>

/** A visit URL (`.../customers/:customerId/visits/:visitId`), looked up by `CustomerVisitGuard` within the customer. */
export const customerVisitParams = z.object({ visitId: z.uuid() })
export type CustomerVisitParams = z.infer<typeof customerVisitParams>

/**
 * A manager adds a client by e-mail, the field the premise pairs clients by (docs/clients.md). The names are written
 * only when the client is new here; an existing or recovered client keeps their details.
 */
export const createCustomerBody = z.object({
  email: personFields.email,
  firstName: z.optional(personFields.firstName),
  lastName: z.optional(personFields.lastName),
})
export type CreateCustomerBody = z.infer<typeof createCustomerBody>

/** `gender: null` is "Neuvedené": the client's gender is not recorded. */
export const updateCustomerBody = z.partial(
  z.object({
    ...personFields,
    gender: z.nullable(personFields.gender),
    loyaltyPoints: customerFields.loyaltyPoints,
  }),
)
export type UpdateCustomerBody = z.infer<typeof updateCustomerBody>

export const setCustomerStatusBody = z.object({ status: z.enum(CustomerStatus) })
export type SetCustomerStatusBody = z.infer<typeof setCustomerStatusBody>

/** `message` is a note the staff writes for the client; it rides out in the block / unblock e-mail. Empty hides it. */
export const setCustomerBlockedBody = z.object({
  blocked: z.boolean(),
  message: z.optional(z.string().check(z.maxLength(2000))),
})
export type SetCustomerBlockedBody = z.infer<typeof setCustomerBlockedBody>

export const setCustomerTrialHoursBody = z.object({ numOfTrials: z.int().check(z.minimum(0)) })
export type SetCustomerTrialHoursBody = z.infer<typeof setCustomerTrialHoursBody>

/** `null` removes the card. */
export const setCustomerCardIdBody = z.object({ cardId: z.nullable(customerFields.cardId) })
export type SetCustomerCardIdBody = z.infer<typeof setCustomerCardIdBody>

const customerVisitFields = {
  /** When the visit happened, as an instant. */
  date: isoDateTime,
  /** Free-form visit details (the note, today); the shape is up to the frontend. */
  data: z.optional(z.record(z.string(), z.unknown())),
}

export const createCustomerVisitBody = z.object(customerVisitFields)
export type CreateCustomerVisitBody = z.infer<typeof createCustomerVisitBody>

export const updateCustomerVisitBody = z.partial(z.object(customerVisitFields))
export type UpdateCustomerVisitBody = z.infer<typeof updateCustomerVisitBody>
